import React, { useCallback, useEffect, useRef, useState } from "react";

import { abrirArchivo, ArchivoParaVer } from "../../../../components/enrollment/visorArchivo";
import { ApiTareasAlumno, ArchivoDeEntrega, DetalleTareaAlumno, EstadoAlumno, PortalApiAlumno, TareaDelAlumno } from "../../../../services/v2/classPortal.service";
import { ArchivosDeLaPublicacion, Icono, mensajeDeError, reducirImagen, subirConBoleto, useAlVolver } from "../comun";
import { ApiQuizAlumno } from "../../../../services/v2/lmsQuiz.service";
import QuizAlumno from "../quiz/quizAlumno";
import { ChipEstadoAlumno, IconoEntrega, puntosTxt, textoEntrega } from "./comunTareas";
import "./tareas.css";

/*
	Tareas del alumno (Portal de Clase, Parte B). Lista (Pendientes, Entregadas, Calificadas) y la pantalla de cada tarea: sus
	instrucciones, su entrega (foto del celular reducida a 1600 px, o archivo; hasta 10 de 25 MB), comentario, Entregar y Deshacer
	mientras el maestro no empiece a calificar. Solo ve su nota y el comentario cuando el maestro la devuelve.
	El alumno nunca manda su id: el servidor lo saca de la sesión.
*/

const MIS_PENDIENTES: EstadoAlumno[] = ["pendiente", "atrasada", "repetir", "quiz"]

export const ListaDeTareasAlumno: React.FC<{ apiT: ApiTareasAlumno; onAbrir: (id: number) => void }> = ({ apiT, onAbrir }) => {
	const [tareas, setTareas] = useState<TareaDelAlumno[] | null>(null)
	const [error, setError] = useState<string | null>(null)
	const cargar = useCallback(() => { apiT.list().then((r) => { setTareas(r.tasks); setError(null) }).catch((e) => setError(mensajeDeError(e, "No pudimos cargar tus tareas."))) }, [apiT])
	useEffect(() => { cargar() }, [cargar])
	useAlVolver(cargar)

	if (error) return <div className="pcAviso pcOro" role="alert">{error}</div>
	if (tareas === null) return <div className="pcCargando">Cargando…</div>
	const grupo = (titulo: string, lista: TareaDelAlumno[]) => lista.length === 0 ? null : (
		<>
			<div className="tkLoteTitulo"><span>{titulo} · {lista.length}</span></div>
			{lista.map((t) => (
				<button key={t.id} type="button" className="tkTareaAlumno" onClick={() => onAbrir(t.id)}>
					<span className={"tkIcono" + (MIS_PENDIENTES.includes(t.state) ? " tkIconoAcento" : "")}><IconoEntrega forma={t.delivery} /></span>
					<span className="tkFilaTextos">
						<span className="tkFilaTitulo" style={{ display: "block" }}>{t.title}</span>
						<span className="tkFilaSub" style={{ display: "block", color: t.state === "atrasada" ? "var(--pc-oro-texto)" : undefined, fontWeight: t.state === "atrasada" ? 700 : undefined }}>
							{[t.unit, t.dueLabel ? "Vence " + t.dueLabel : textoEntrega(t.delivery), t.delivery === "sin_entrega" ? null : puntosTxt(t.points) + " Pts"].filter(Boolean).join(" · ")}
						</span>
					</span>
					<ChipEstadoAlumno estado={t.state} score={t.score} scoreHidden={t.scoreHidden} max={t.pointsMax} />
				</button>
			))}
		</>
	)
	const pendientes = tareas.filter((t) => MIS_PENDIENTES.includes(t.state))
	const entregadas = tareas.filter((t) => t.state === "entregada")
	const calificadas = tareas.filter((t) => t.state === "devuelta")
	const otras = tareas.filter((t) => ["cerrada", "en_clase", "lectura"].includes(t.state))
	return (
		<>
			{tareas.length === 0 ? <div className="pcVacio">Aún No Hay Tareas</div> : null}
			{grupo("Pendientes", pendientes)}
			{grupo("Entregadas", entregadas)}
			{grupo("Calificadas", calificadas)}
			{grupo("Otras", otras)}
		</>
	)
}

const paraVisor = (apiT: ApiTareasAlumno, postId: number, f: ArchivoDeEntrega): ArchivoParaVer => ({
	nombre: f.name, mime: f.mime, origen: "Portal de Clase", urlLista: f.isImage ? f.url : null, descargar: false,
	obtenerUrl: async () => (await apiT.fileUrl(postId, f.id)).url,
	cargar: async () => {
		const r = await fetch((await apiT.fileUrl(postId, f.id)).url)
		if (!r.ok) throw new Error("No se pudo leer el archivo")
		return { blob: await r.blob(), nombre: f.name }
	},
})

interface PropsDetalle { apiT: ApiTareasAlumno; api: PortalApiAlumno; apiQ?: ApiQuizAlumno; postId: number; onVolver: () => void; onCambio: () => void }

export const DetalleDeTarea: React.FC<PropsDetalle> = ({ apiT, api, apiQ, postId, onVolver, onCambio }) => {
	const [t, setT] = useState<DetalleTareaAlumno | null>(null)
	const [error, setError] = useState<string | null>(null)
	const [aviso, setAviso] = useState<string | null>(null)
	const [comentario, setComentario] = useState<string>("")
	const [subiendo, setSubiendo] = useState<{ nombre: string; porciento: number } | null>(null)
	const [ocupado, setOcupado] = useState<boolean>(false)
	const camara = useRef<HTMLInputElement>(null)
	const galeria = useRef<HTMLInputElement>(null)
	const pedido = useRef<boolean>(false)
	const avisoRef = useRef<HTMLDivElement>(null)
	useEffect(() => { if (aviso && avisoRef.current) avisoRef.current.scrollIntoView({ block: "center", behavior: "smooth" }) }, [aviso])

	const cargar = useCallback(() => {
		apiT.get(postId).then((d) => { setT(d); setError(null); if (!pedido.current) { setComentario(d.submission.comment || ""); pedido.current = true } })
			.catch((e) => setError(mensajeDeError(e, "No pudimos abrir esta tarea.")))
	}, [apiT, postId])
	useEffect(() => { pedido.current = false; cargar() }, [cargar])
	useAlVolver(() => { if (!subiendo && !ocupado) cargar() })

	if (error && !t) return <div className="pc"><div className="pcPagina pcPaginaAlumno"><button type="button" className="pcEnlaceBtn" onClick={onVolver}>Mis Tareas</button><div className="pcAviso pcOro" role="alert">{error}</div></div></div>
	if (!t) return <div className="pc"><div className="pcCargando">Cargando…</div></div>
	// Parte D: un quiz tiene su propia pantalla (portada → preguntas → entregar).
	if (t.delivery === "quiz" && apiQ) return <QuizAlumno apiQ={apiQ} postId={postId} onVolver={onVolver} onCambio={onCambio} />

	const sub = t.submission
	const entregable = t.delivery === "foto" || t.delivery === "archivo"
	const vencida = t.dueMs !== null && t.serverNowMs > t.dueMs

	const subir = async (lista: FileList | null) => {
		if (!lista || !t) return
		setError(null); setAviso(null)
		const archivos = Array.from(lista)
		if (sub.files.length + archivos.length > t.limits.maxFiles) { setAviso("Máximo " + t.limits.maxFiles + " archivos por entrega."); return }
		let actual: DetalleTareaAlumno = t
		for (const original of archivos) {
			try {
				const f = t.delivery === "foto" ? await reducirImagen(original, 1600) : original
				if (f.size > t.limits.maxMb * 1048576) { setAviso("\"" + f.name + "\" pesa más de " + t.limits.maxMb + " MB."); continue }
				setSubiendo({ nombre: f.name, porciento: 0 })
				const key = await subirConBoleto((tipo) => apiT.uploadUrl(postId, { name: f.name, size: f.size, mime: tipo }), f, (p) => setSubiendo({ nombre: f.name, porciento: p }))
				actual = await apiT.addFiles(postId, [{ key, name: f.name, mime: f.type || "application/octet-stream", size: f.size }])
				setT(actual)
			} catch (e) { setAviso(mensajeDeError(e, "No se pudo subir \"" + original.name + "\".")) }
		}
		setSubiendo(null)
		if (camara.current) camara.current.value = ""
		if (galeria.current) galeria.current.value = ""
	}
	const quitar = async (f: ArchivoDeEntrega) => {
		setAviso(null)
		try { setT(await apiT.removeFile(postId, f.id)) } catch (e) { setAviso(mensajeDeError(e)) }
	}
	const entregar = async () => {
		setOcupado(true); setAviso(null)
		try { setT(await apiT.submit(postId, comentario.trim())); onCambio() } catch (e) { setAviso(mensajeDeError(e)) } finally { setOcupado(false) }
	}
	const deshacer = async () => {
		setOcupado(true); setAviso(null)
		try { const d = await apiT.undo(postId); setT(d); setComentario(d.submission.comment || ""); onCambio() } catch (e) { setAviso(mensajeDeError(e)) } finally { setOcupado(false) }
	}
	const ver = (f: ArchivoDeEntrega) => abrirArchivo(paraVisor(apiT, postId, f))

	const g = t.grade
	return (
		<div className="pc">
			<div className="pcPagina pcPaginaAlumno">
				<div style={{ display: "flex", alignItems: "center", gap: 6 }}>
					<button type="button" className="pcIb" style={{ marginLeft: -10 }} aria-label="Volver a las tareas" onClick={onVolver}><Icono n="atras" size={22} /></button>
					<span style={{ flex: 1, fontWeight: 700, fontSize: 15 }}>Mis Tareas</span>
				</div>

				<div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
					<div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
						{t.unit ? <span className="pcChip">{t.unit}</span> : null}
						{t.delivery !== "sin_entrega" ? <span className="pcChip">{puntosTxt(t.points)} Pts</span> : null}
						<span className="pcChip pcChipBorde">{textoEntrega(t.delivery)}</span>
						{t.state === "entregada" ? <span className="pcChip tkChipOscuro">Entregada</span> : null}
						{t.state === "repetir" ? <span className="pcChip pcChipOro">Por Repetir</span> : null}
					</div>
					<h1 className="pcT" style={{ margin: 0, fontSize: 26, fontWeight: 800, lineHeight: 1.1, overflowWrap: "anywhere" }}>{t.title}</h1>
					{t.dueLabel ? <div style={{ fontSize: 14, fontWeight: 700, color: vencida && t.state !== "entregada" && t.state !== "devuelta" ? "var(--pc-oro-texto)" : "var(--pc-acento-texto)" }}>
						{t.delivery === "en_clase" ? t.dueLabel : "Vence " + t.dueLabel}</div> : null}
				</div>

				{sub.repeatNote && !t.grade?.kept ? <div className="tkAvisoOro" role="status">Tu maestro te pidió repetir la entrega: {sub.repeatNote}</div> : null}
				{entregable && vencida && t.state !== "entregada" && t.state !== "devuelta" ? (
					<div className="tkAvisoOro" role="status">{t.acceptLate ? "Ya venció" + (t.latePenaltyPct > 0 ? ": si entregas ahora se te resta " + puntosTxt(t.latePenaltyPct) + "% por cada día tarde." : ". Todavía se acepta tu entrega.") : (t.state === "repetir" ? "Esta entrega ya había cerrado, pero tu maestro te pidió repetirla." : "La entrega de esta tarea ya cerró.")}</div>
				) : null}

				{t.body ? <div className="pcTarjeta" style={{ fontSize: 14.5, lineHeight: 1.55, whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{t.body}</div> : null}
				<ArchivosDeLaPublicacion api={api} archivos={t.files} esAlumno />

				{g ? (
					<div className="pcTarjeta tkMiNota" aria-label="Tu nota">
						<div className="pcEtiqueta">Tu Nota</div>
						{g.points !== null ? (
							<div><span className={"tkNumeroGrande" + (g.points / t.points < 0.7 ? " tkNumeroOro" : "")}>{puntosTxt(g.points)}</span> <span className="pcTenue" style={{ fontWeight: 700 }}>/ {puntosTxt(t.points)}</span></div>
						) : <div className="pcTenue">Tu maestro ya la devolvió. Tu nota se publica más adelante.</div>}
						{g.kept ? <div className="pcTenue pcChico" style={{ fontWeight: 600 }}>Nota Anterior: No Se Repitió</div> : null}
						{g.lateDiscount > 0 ? <div className="pcTenue pcChico">Incluye un descuento de {puntosTxt(g.lateDiscount)} pts por entrega tarde.</div> : null}
						{g.criteria ? <div style={{ textAlign: "left", display: "flex", flexDirection: "column", gap: 6 }}>{g.criteria.map((c, i) => <div className="tkDetalleCriterio" key={i}><span>{c.name}</span><b>{puntosTxt(c.points)} / {puntosTxt(c.max)}</b></div>)}</div> : null}
						{g.comment ? <div className="tkComentarioAlumno" style={{ textAlign: "left" }}><b>Tu Maestro:</b> {g.comment}</div> : null}
					</div>
				) : null}

				{entregable ? (
					<>
						<div style={{ display: "flex", alignItems: "center" }}>
							<span className="pcEtiqueta" style={{ flex: 1 }}>Tu Entrega</span>
							<span className="pcTenue pcChico" style={{ fontWeight: 700 }}>{sub.files.length} De {t.limits.maxFiles} {t.delivery === "foto" ? "Fotos" : "Archivos"}</span>
						</div>
						{t.delivery === "foto" ? (
							<div className="tkMisFotos">
								{sub.files.map((f, i) => (
									<div className="tkFoto" key={f.id}>
										{f.isImage && f.url ? <img src={f.url} alt={"Tu foto " + (i + 1)} /> : null}
										<button type="button" style={{ position: "absolute", inset: 0, opacity: 0, background: "none", border: 0, cursor: "pointer" }} aria-label={"Ver foto " + (i + 1)} onClick={() => ver(f)} />
										{t.canEdit ? <button type="button" className="tkQuitar" aria-label={"Quitar foto " + (i + 1)} onClick={() => quitar(f)}><Icono n="cerrar" size={18} /></button> : null}
									</div>
								))}
								{t.canEdit && sub.files.length < t.limits.maxFiles ? (
									<button type="button" className="tkAgregarFoto" disabled={!!subiendo} onClick={() => camara.current?.click()}><Icono n="camara" size={24} />Tomar Foto</button>
								) : null}
							</div>
						) : (
							<div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
								{sub.files.map((f) => (
									<div className="tkArchivoMio" key={f.id}>
										<button type="button" onClick={() => ver(f)}><Icono n="doc" /><span className="pcNombre">{f.name}</span></button>
										{t.canEdit ? <button type="button" className="pcIb" style={{ width: 40, height: 40 }} aria-label={"Quitar " + f.name} onClick={() => quitar(f)}><Icono n="cerrar" size={18} /></button> : null}
									</div>
								))}
							</div>
						)}
						{t.canEdit ? (
							<>
								<input ref={camara} type="file" accept="image/*" capture="environment" hidden onChange={(e) => subir(e.target.files)} />
								<input ref={galeria} type="file" multiple accept={t.delivery === "foto" ? "image/*" : undefined} hidden onChange={(e) => subir(e.target.files)} />
								<button type="button" className="pcBtn pcBtnS" style={{ minHeight: 48 }} disabled={!!subiendo || sub.files.length >= t.limits.maxFiles} onClick={() => galeria.current?.click()}>
									{t.delivery === "foto" ? "Subir Desde Galería" : "Subir Archivo"}
								</button>
								{/* El aviso (foto muy pesada, PDF en tarea de fotos) va junto al botón: abajo quedaba detrás de la barra "Entregar" (QA 2026-10-21). */}
								{aviso ? <div className="tkAvisoOro" role="alert" ref={avisoRef}>{aviso}</div> : null}
								{subiendo ? <div><div className="pcChico pcTenue">Subiendo {subiendo.nombre} {subiendo.porciento}%</div><div className="pcBarra"><div style={{ width: subiendo.porciento + "%" }} /></div></div> : null}
								<div className="pcCampo"><label htmlFor="tkCom" className="pcEtiqueta">Comentario Para Tu Maestro</label>
									<textarea id="tkCom" className="pcTextarea" rows={2} placeholder="Opcional" maxLength={2000} value={comentario} onChange={(e) => setComentario(e.target.value)} /></div>
							</>
						) : sub.comment ? (
							<div className="tkComentarioAlumno"><b>Tu Comentario:</b> {sub.comment}</div>
						) : null}
						{sub.late && sub.submittedLabel ? <div className="tkAvisoOro">Entregaste tarde ({sub.lateDays} {sub.lateDays === 1 ? "día" : "días"}).</div> : null}
					</>
				) : t.delivery === "en_clase" ? (
					<div className="pcTarjeta">Esta práctica se califica en clase: tu maestro te pone la nota. No tienes que subir nada.</div>
				) : t.delivery === "quiz" ? (
					<div className="pcTarjeta">El quiz llega pronto a Mi Clase.</div>
				) : <div className="pcTarjeta">Esta tarea es solo de lectura: no se entrega.</div>}

				{t.rubric.length > 0 ? (
					<details className="pcTarjeta" style={{ padding: 14 }}>
						<summary style={{ fontWeight: 700, fontSize: 14.5, cursor: "pointer", minHeight: 24 }}>Cómo Se Califica</summary>
						<div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 10, fontSize: 14 }}>
							{t.rubric.map((c, i) => <div className="tkDetalleCriterio" key={i}><span>{c.name}</span><b>{puntosTxt(c.points)}</b></div>)}
						</div>
					</details>
				) : null}

				{aviso && !(entregable && t.canEdit) ? <div className="tkAvisoOro" role="alert" ref={avisoRef}>{aviso}</div> : null}

				{entregable && (t.canEdit || t.canUndo) ? (
					<div className="tkPieEntrega">
						{t.canEdit ? <button type="button" className="pcBtn pcBtnP" disabled={ocupado || !!subiendo || sub.files.length === 0} onClick={entregar}>{t.state === "repetir" ? "Entregar De Nuevo" : "Entregar"}</button> : null}
						{!t.canEdit && t.canUndo ? <button type="button" className="pcBtn pcBtnS" disabled={ocupado} onClick={deshacer}>Entregada · Deshacer</button> : null}
					</div>
				) : null}
			</div>
		</div>
	)
}
